<template>
    <modal
        name="patient-enrollment"
        height="auto"
        class="pt-5 pb-5"
        :scrollable="true"
        width="60%"
    >
        <div class="p-4 d-flex justify-content-between h-100 flex-column">
            <div v-if="study.hasStratums">
                <div
                    v-for="stratum in stratums"
                    :key="stratum.key"
                >
                    <h3>{{ stratum.label }}</h3>
                    <table class="table table-bordered">
                        <thead>
                            <tr>
                                <th>Group</th>
                                <th
                                    v-for="(column, index) in columns[stratum.key]"
                                    :key="stratum.key + '-' + index"
                                >
                                    <div
                                        class="d-flex align-items-center"
                                    >
                                        <input
                                            v-model="columns[stratum.key][index]"
                                            class="form-control font-weight-bold"
                                            type="text"
                                        >
                                        <font-awesome-icon
                                            icon="trash"
                                            class="clickable ml-2"
                                            @click="deleteColumn(stratum.key, column)"
                                        />

                                        <input
                                            v-model="includeInEnrollmentCount[stratum.key]"
                                            :value="column"
                                            class="ml-2"
                                            type="checkbox"
                                        >
                                    </div>
                                </th>
                                <th>
                                    <font-awesome-icon
                                        icon="plus"
                                        class="clickable ml-2"
                                        @click="addColumn(stratum.key)"
                                    />
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr
                                v-for="(groupedEnrollment, index) in groupedEnrollments.filter(ge => ge.some(e => e.stratum === stratum.key))"
                                :key="stratum.key + '-' + index"
                            >
                                <td>{{ groupedEnrollment[0].group.name }}</td>
                                <td
                                    v-for="enrollment in groupedEnrollment.filter(ge => ge.stratum === stratum.key)"
                                    :key="enrollment.id"
                                >
                                    <input
                                        v-model="enrollment.value"
                                        type="number"
                                        step="1"
                                        class="form-control"
                                    >
                                </td>
                                <td>
                                    <font-awesome-icon
                                        icon="trash"
                                        class="clickable ml-2"
                                        @click="deleteEnrollment(groupedEnrollment[0].groupId, stratum)"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div
                        v-if="getAvailableGroups(stratum).length > 0"
                        class="d-flex mb-3"
                    >
                        <select
                            v-model="selectedGroup[stratum.key]"
                            class="form-control mr-2 group-selector"
                        >
                            <option
                                :value="undefined"
                                disabled
                                selected
                            >
                                Please select a group...
                            </option>
                            <option
                                v-for="group in getAvailableGroups(stratum)"
                                :key="group.id"
                                :value="group"
                            >
                                {{ group.name }}
                            </option>
                        </select>
                        <button
                            class="btn btn-success"
                            :disabled="!selectedGroup[stratum.key]"
                            @click="addGroup(stratum.key)"
                        >
                            Add Group
                        </button>
                    </div>
                </div>
            </div>

            <div v-else>
                <table class="table table-bordered">
                    <thead>
                        <tr>
                            <th>Group</th>
                            <th
                                v-for="(column, index) in columns[0]"
                                :key="index"
                            >
                                <div
                                    class="d-flex align-items-center"
                                >
                                    <input
                                        v-model="columns[0][index]"
                                        class="form-control font-weight-bold"
                                        type="text"
                                    >

                                    <font-awesome-icon
                                        icon="trash"
                                        class="clickable ml-2"
                                        @click="deleteColumn(0, column)"
                                    />

                                    <input
                                        v-model="includeInEnrollmentCount[0]"
                                        :value="column"
                                        class="ml-2"
                                        type="checkbox"
                                    >
                                </div>
                            </th>
                            <th>
                                <font-awesome-icon
                                    icon="plus"
                                    class="clickable ml-2"
                                    @click="addColumn(0)"
                                />
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr
                            v-for="groupedEnrollment in groupedEnrollments"
                            :key="groupedEnrollment.groupId"
                        >
                            <td>{{ groupedEnrollment[0].group.name }}</td>
                            <td
                                v-for="enrollment in groupedEnrollment"
                                :key="enrollment.id"
                            >
                                <input
                                    v-model="enrollment.value"
                                    type="number"
                                    step="1"
                                    class="form-control"
                                >
                            </td>
                            <td>
                                <font-awesome-icon
                                    icon="trash"
                                    class="clickable ml-2"
                                    @click="deleteEnrollment(groupedEnrollment[0].groupId)"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div
                    v-if="getAvailableGroups().length > 0"
                    class="d-flex mb-3"
                >
                    <select
                        v-model="selectedGroup[0]"
                        class="form-control mr-2 group-selector"
                    >
                        <option
                            :value="undefined"
                            disabled
                            selected
                        >
                            Please select a group...
                        </option>
                        <option
                            v-for="group in getAvailableGroups()"
                            :key="group.id"
                            :value="group"
                        >
                            {{ group.name }}
                        </option>
                    </select>
                    <button
                        class="btn btn-success"
                        :disabled="!selectedGroup[0]"
                        @click="addGroup(0)"
                    >
                        Add Group
                    </button>
                </div>
            </div>

            <div class="d-flex">
                <button
                    class="btn btn-secondary mr-3"
                    @click="$modal.hide('patient-enrollment')"
                >
                    <span>Cancel</span>
                </button>

                <button
                    class="btn btn-primary"
                    @click="$emit('save')"
                >
                    <span>Save</span>
                </button>
            </div>
        </div>
    </modal>
</template>

<script>
import Vue from 'vue';
import groupService from '../services/group.service';
import stratums from '../data/stratums';

export default {
    name: 'PatientEnrollmentModal',

    props: {
        study: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            stratums,
            selectedGroup: [],
            groups: [],
            columns: [[], []],
            includeInEnrollmentCount: [[], []],
        };
    },

    computed: {
        groupedEnrollments() {
            return Object.values(this._.groupBy(this.study.patientEnrollments, 'groupId'));
        },

        jsonColumns() {
            return JSON.stringify(this.columns);
        },

        jsonIncludeInEnrollmentCount() {
            return JSON.stringify(this.includeInEnrollmentCount);
        },
    },

    watch: {
        study() {
            this.stratums.forEach((stratum) => {
                Vue.set(this.columns, stratum.key, [...new Set(this.study.patientEnrollments.filter((pe) => pe.stratum === stratum.key).map((pe) => pe.label))]);
                Vue.set(this.includeInEnrollmentCount, stratum.key, [...new Set(this.study.patientEnrollments.filter((pe) => pe.stratum === stratum.key && pe.includeInEnrollmentCount).map((pe) => pe.label))]);

                // If there are no columns for a stratum, add an empty column.
                if (this.columns[stratum.key].length === 0) {
                    this.columns[stratum.key].push(`Column ${this.columns[stratum.key].length + 1}`);
                }
            });
        },

        jsonColumns(newValueString, oldValueString) {
            const newValue = JSON.parse(newValueString);
            const oldValue = JSON.parse(oldValueString);

            oldValue.forEach((stratum, stratumIndex) => {
                stratum.forEach((oldColumn, index) => {
                    const newColumn = newValue[stratumIndex][index];

                    if (this.includeInEnrollmentCount[stratumIndex].includes(oldColumn)) {
                        const indexOfOldColumn = this.includeInEnrollmentCount[stratumIndex].indexOf(oldColumn);

                        if (indexOfOldColumn !== -1) {
                            this.includeInEnrollmentCount[stratumIndex][indexOfOldColumn] = newColumn;
                        }
                    }

                    this.study.patientEnrollments.forEach((pe) => {
                        if (pe.label === oldColumn) {
                            pe.label = newColumn;
                        }
                    });
                });
            });
        },

        jsonIncludeInEnrollmentCount(newValueString) {
            const newValue = JSON.parse(newValueString);

            newValue.forEach((stratum, stratumIndex) => {
                this.study.patientEnrollments.filter((pe) => pe.stratum === stratumIndex).forEach((pe) => {
                    pe.includeInEnrollmentCount = newValue[stratumIndex].includes(pe.label);
                });
            });
        },
    },

    mounted() {
        this.getGroups().then((r) => {
            this.groups = r.data;
        });
    },

    methods: {
        getGroups: groupService.getGroups,

        deleteEnrollment(groupId, stratum) {
            if (stratum) {
                const newEnrollments = [];

                _.each(this.study.patientEnrollments, (pe) => {
                    if (pe.groupId !== groupId || pe.stratum !== stratum.key) {
                        newEnrollments.push(pe);
                    }
                });

                this.study.patientEnrollments = newEnrollments;
            } else {
                this.study.patientEnrollments = this._.filter(this.study.patientEnrollments, (pe) => pe.groupId !== groupId);
            }
        },

        getAvailableGroups(stratum) {
            if (stratum) {
                return this._.filter(this.groups, (s) => !this._.some(this._.filter(this.study.patientEnrollments, (pe) => pe.stratum === stratum.key), (pe) => pe.groupId === s.id));
            }

            return this._.filter(this.groups, (s) => !this._.some(this.study.patientEnrollments, (pe) => pe.groupId === s.id));
        },

        addGroup(stratum) {
            if (this.selectedGroup[stratum] != null) {
                this.columns[stratum].forEach((c) => {
                    const enrollment = {
                        studyId: this.study.id,
                        group: { ...this.selectedGroup[stratum] },
                        groupId: this.selectedGroup[stratum].id,
                        stratum,
                        label: c,
                        value: 0,
                    };

                    this.study.patientEnrollments.push(enrollment);
                });
                this.selectedGroup[stratum] = null;
            }
        },

        addColumn(stratum) {
            const newColumnName = `Column ${this.columns[stratum].length + 1}`;

            Vue.set(this.columns[stratum], this.columns[stratum].length, newColumnName);

            this.groupedEnrollments.filter((ge) => ge.some((e) => e.stratum === stratum)).forEach((pe) => {
                const enrollment = {
                    studyId: this.study.id,
                    group: { ...pe[0].group },
                    groupId: pe[0].groupId,
                    label: newColumnName,
                    stratum,
                    value: 0,
                };

                this.study.patientEnrollments.push(enrollment);
            });
        },

        deleteColumn(stratum, column) {
            Vue.set(this.columns, stratum, this._.filter(this.columns[stratum], (c) => c !== column));

            this.study.patientEnrollments = this.study.patientEnrollments.filter((x) => x.label !== column || x.stratum !== stratum);
        },
    },
};
</script>

<style lang="css" scoped>
.group-selector {
  width: 300px;
}
</style>
